import { IScope, IQService, IPromise, ITimeoutService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalServiceInstance, IModalService } from "angular-ui-bootstrap";
import dayjs from "@gtmhub/dayjs";
import {
  IKpi,
  IKpiSnapshot,
  IKpiGroup,
  IKpiGroupSetting,
  IKpiCadence,
  IKpisScreenSettings,
  INewKpiSnapshot,
  IKpiSnapshotPatternMap,
  IGetKpiSnapshotsQueryParams,
  KpiUnitTooltipPlacement,
  SnapshotProgressLabel,
  ICreateKpiSnapshotPayload,
  IKpiExistingUiGroup,
  IKpiNewGroup,
  KpiUiGroup,
  IKpiSystemGroup,
  IModifiableKpi,
} from "../models/models";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { KpiService } from "../services/kpiService";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { IKpiGroupingFormOpener } from "./grouping-form";
import { AccountType, IAccount } from "@gtmhub/core";
import { IUnitFormatting } from "@gtmhub/shared/models";
import { IStateInit } from "@gtmhub/core/routing";
import { storage } from "@gtmhub/core/storage";
import { IIndicator } from "@gtmhub/error-handling";
import { INgRedux } from "@gtmhub/state-management";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { IdMap, toKeyMap } from "@gtmhub/util";
import { byTextField } from "@gtmhub/util/sorting";
import { NavigationEvents } from "@gtmhub/navigation";
import { offset } from "@gtmhub/shared/dom";
import { IKpiCadenceRange, DATE_FORMAT, dateRangeForCadence, generateSnapshotRange, isMac } from "../util";
import { getCurrentUserId } from "@gtmhub/users";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { ViewHistoryService } from "@gtmhub/view-history";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { FeatureFlag } from "@gtmhub/featureToggle/models";

export interface IKpiItems {
  kpi: IKpi;
  snapshots: IdMap<IKpiSnapshotItems>;
  loadingSnapshots: boolean;
}

interface IKpiSnapshotItems {
  snapshot: IKpiSnapshot;
  creating: boolean;
}

interface IKpisScreenSizes {
  table: IKpisTableSizes;
  headerHeight: number;
  allGroupsHeader: number;
  favoriteGroupsHeader: number;
  sideBarWidth: number;
  sideBarCollapsedWidth: number;
  demoBannerHeight: number;
  trialBannerHeight: number;
  defaultTableTop: number;
}

interface IKpisTableSizes {
  leftColWidth: number;
  colWidth: number;
  headerHeight: number;
  headerContentPadding: number;
  rowHeight: number;
  offsetBottom: number;
  paddingBottom: number;
  paddingRight: number;
  borderWidth: number;
}

interface IKpisTableDimensions {
  bottom: number;
  borderBottom: number;
}

interface IGroupCell {
  targetType: "group";
  groupId: string;
  id: string;
}

export interface IKpiCell {
  targetType: "kpi";
  kpiId: string;
  groupId: string;
  id: string;
}

interface ICadenceCell {
  targetType: "cadence";
  cadenceKey: string;
  groupId: string;
  id: string;
}

export interface ISnapshotCell {
  targetType: "snapshot";
  kpiId: string;
  cadenceKey: string;
  groupId: string;
  id: string;
}

interface IKpiCadenceData {
  items: IKpiCadence[];
  range: IKpiCadenceRange;
}

type GroupOrKpiCell = IGroupCell | IKpiCell;
type CadenceOrSnapshotCell = ICadenceCell | ISnapshotCell;

export interface IKpiTable {
  stickyColumn: GroupOrKpiCell[];
  columns: CadenceOrSnapshotCell[][];
}

interface IKpisScreenTimeouts {
  scrolling: IPromise<void>;
  resizing: IPromise<void>;
}
export interface IKpisScope extends IScope, IExtendedRootScopeService {
  kpiTable: IKpiTable;
  favoriteGroupsWithKpis: GroupOrKpiCell[];
  showElements: {
    emptyState: boolean;
    kpiTable: boolean;
  };
  cadenceKeyMap: IdMap<IKpiCadence>;
  indicators: {
    loading?: IIndicator;
    gettingSnapshots?: IIndicator;
    creatingSnapshot?: IIndicator;
    deletingKpi?: IIndicator;
    patchingGroup?: IIndicator;
    creatingGroup?: IIndicator;
    deletingGroup?: IIndicator;
    addingGroupToFavorites?: IIndicator;
    removingGroupFromFavorites?: IIndicator;
  };
  tableSizes: IKpisTableDimensions;
  kpisMap: IdMap<IKpiItems>;
  groupsMap: IdMap<KpiUiGroup>;
  assigneesIdMap: IAssigneeIdMap;
  screenSettings: IKpisScreenSettings;
  newSnapshot: INewKpiSnapshot;
  scrollToCellId: string;
  allGroupsExpanded: boolean;
  favoriteGroupsExpanded: boolean;
  isDemoAccount: boolean;
  isTrialAccount: boolean;
  onScreenSettingsChanged(): void;
  openKpiForm(kpi?: IKpi): void;
  openKpiGroupingForm(kpi: IKpi): void;
  onSnapshotKeydown($event: JQueryEventObject, kpiId: string, cadenceKey: string, groupId: string): void;
  onSnapshotBlur($event: JQueryEventObject, kpiId: string, cadenceKey: string): void;
  onGroupKeydown($event: JQueryEventObject, groupId: string): void;
  onGroupBlur($event: JQueryEventObject, groupId: string): void;
  openConfirmDeleteKpiModal(kpi: IKpi): void;
  openCreateSnapshotForm(kpiId: string, cadence: string, groupId: string): void;
  closeCreateSnapshotForm(kpiId: string, cadence: string): void;
  validateSnapshotInput(input: string): void;
  toggleGroup(group: KpiUiGroup): void;
  hasManageDataPermission(): boolean;
  beginNewGroup(): void;
  openConfirmDeleteGroupModal(group: IKpiGroup): void;
  addGroupToFavorites(groupId: string, cellId: string): void;
  addGroupToFavoritesHelper(addedToFavoritesGroup: IKpiExistingUiGroup): void;
  removeGroupFromFavorites(groupId: string): void;
  removeGroupFromFavoritesHelper(addedToFavoritesGroup: IKpiExistingUiGroup): void;
  scrollToFavoriteGroup(cellId: string): void;
  expandFavoriteGroups(): void;
  expandAllGroups(): void;
  move(kpiCell: IKpiCell, action: "top" | "up" | "down" | "bottom", isFavourites: boolean): void;
  showMoveActions(kpiCell: IKpiCell, isFavourites: boolean);
  hideMoveActions(kpiCell: IKpiCell, isFavourites: boolean);
  triggerCellBackgroundColor(kpiCell: IKpiCell, isFavourites: boolean);
  resetIndicator(indicator: keyof IKpisScope["indicators"]): void;
  isSharedKpisEnabled: boolean;
  isMac: boolean;
}

export const dateFormatsMap: IKpiSnapshotPatternMap = {
  day: { key: "YYYY-MM-DD", viewVal: "DD, dddd" },
  week: { key: "GGGG-[W]WW", viewVal: "[Week] WW, GGGG" },
  month: { key: "YYYY-MM", viewVal: "MMMM, YYYY" },
  quarter: { key: "YYYY-[Q]Q", viewVal: "[Q]Q, YYYY" },
  year: { key: "YYYY", viewVal: "YYYY" },
};
export const MY_KPIS_GROUP_ID = "mykpis";
export const ungroupedGroupId = "ungrouped";
export const newGroupId = "newgroup";
export const allGroupsId = "allgroups";
export const favGroupsId = "favoritegroups";
// this variable will be removed if we implement groups ordering and persisting
export const expandedGroupsStorageKey = "expandedKpiGroups";
export const favExpandedGroupsStorageKey = "favExpandedKpiGroups";
export const minCadenceCount = 3;
export const futureCadencesDisplayedInitially = 1;
const kpiTableSelector = "#kpi-table";

const bindStateToScope = (state: IAssigneesStoreState) => {
  return {
    assigneesIdMap: state.assignees.map,
  };
};

export class KpisCtrl implements IStateInit {
  private timeZone: string;
  private cadences: IKpiCadenceData;
  private cadencesPerRequest: number;
  // this will be removed when settings are persisted in user or account settings
  // all its occurrences shall be then replaced with persisted setting and logic will need to be slightly changed
  private expandedGroupsIdSet: IdMap<boolean> = storage.get(expandedGroupsStorageKey) || {};
  private favExpandedGroupsIdSet: IdMap<boolean> = storage.get(favExpandedGroupsStorageKey) || {};
  private kpiIdFetchedSnapshotsSet: IdMap<boolean> = {};
  private orderedGroupsKpis: IKpiGroupSetting[];
  private timeouts: IKpisScreenTimeouts = { resizing: null, scrolling: null };
  private loadMoreSnapshotsPromise: IPromise<unknown>;
  private extendingTable: boolean;
  private screenSizes: IKpisScreenSizes = {
    table: {
      leftColWidth: 305,
      colWidth: 160,
      headerHeight: 50,
      rowHeight: 76,
      offsetBottom: 85,
      headerContentPadding: 13,
      paddingBottom: 15,
      paddingRight: 15,
      borderWidth: 1,
    },
    headerHeight: 75, // 40px height + 35px padding
    allGroupsHeader: 50,
    favoriteGroupsHeader: 50,
    sideBarWidth: 220,
    sideBarCollapsedWidth: 60,
    demoBannerHeight: 50,
    trialBannerHeight: 50,
    defaultTableTop: 77,
  };
  private isReorderKpisFeatureEnabled = false;

  private openedSnapshot: IKpiSnapshotItems = null;
  private confirmDeleteKpiModal: IModalServiceInstance;
  private confirmDeleteGroupModal: IModalServiceInstance;
  public static $inject = [
    "$scope",
    "$rootScope",
    "$q",
    "$uibModal",
    "$state",
    "$timeout",
    "KpiService",
    "CurrentUserService",
    "AccountResolverService",
    "KpiGroupingFormOpener",
    "$ngRedux",
    "AssigneeActions",
    "FeatureToggleV2Service",
    "ViewHistoryService",
    "AnalyticsService",
  ];

  constructor(
    private $scope: IKpisScope,
    private $rootScope: IGtmhubRootScopeService,
    private $q: IQService,
    private $uibModal: IModalService,
    private $state: StateService,
    private $timeout: ITimeoutService,
    private kpiService: KpiService,
    private currentUserService: CurrentUserService,
    private accountResolverService: AccountResolverService,
    private kpiGroupingFormOpener: IKpiGroupingFormOpener,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions,
    private featureToggleV2Service: FeatureToggleV2Service,
    private viewHistoryService: ViewHistoryService,
    private analyticsService: AnalyticsService
  ) {}

  public stateInit(): IPromise<unknown> {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    if (!this.$rootScope.hasPermission("ManageData") && !this.$rootScope.hasPermission("AccessInsightboards")) return;

    return this.init();
  }

  private setTimezone = (): string => {
    const account: IAccount = this.accountResolverService.getAccountData();

    return account && account.notifications && account.notifications.timezone ? account.notifications.timezone : "UTC+0";
  };

  private init = (): IPromise<unknown> => {
    // needed when visiting an insight via KPI form and clicking back button
    this.$rootScope.noNavigation = false;

    this.$scope.onScreenSettingsChanged = this.onScreenSettingsChanged;
    this.$scope.openKpiForm = this.openKpiForm;
    this.$scope.openKpiGroupingForm = this.openKpiGroupingForm;
    this.$scope.onSnapshotKeydown = this.onSnapshotKeydown;
    this.$scope.onSnapshotBlur = this.onSnapshotBlur;
    this.$scope.onGroupKeydown = this.onGroupKeydown;
    this.$scope.onGroupBlur = this.onGroupBlur;
    this.$scope.openConfirmDeleteKpiModal = this.openConfirmDeleteKpiModal;
    this.$scope.openCreateSnapshotForm = this.openCreateSnapshotForm;
    this.$scope.closeCreateSnapshotForm = this.closeCreateSnapshotForm;
    this.$scope.validateSnapshotInput = this.validateSnapshotInput;
    this.$scope.toggleGroup = this.toggleGroup;
    this.$scope.hasManageDataPermission = this.hasManageDataPermission;
    this.$scope.beginNewGroup = this.beginNewGroup;
    this.$scope.openConfirmDeleteGroupModal = this.openConfirmDeleteGroupModal;
    this.$scope.addGroupToFavorites = this.addGroupToFavorites;
    this.$scope.addGroupToFavoritesHelper = this.addGroupToFavoritesHelper;
    this.$scope.removeGroupFromFavorites = this.removeGroupFromFavorites;
    this.$scope.removeGroupFromFavoritesHelper = this.removeGroupFromFavoritesHelper;
    this.$scope.scrollToFavoriteGroup = this.scrollToFavoriteGroup;
    this.$scope.expandFavoriteGroups = this.expandFavoriteGroups;
    this.$scope.expandAllGroups = this.expandAllGroups;
    this.$scope.move = this.move;
    this.$scope.showMoveActions = this.showMoveActions;
    this.$scope.hideMoveActions = this.hideMoveActions;
    this.$scope.triggerCellBackgroundColor = this.triggerCellBackgroundColor;

    this.timeZone = this.setTimezone();

    this.$scope.$on("kpiCreated", (evt, kpi: IKpi) => this.onKpiCreated(kpi));
    this.$scope.$on("kpiUpdated", (evt, kpi: IKpi, kpisGroupOrderToUpdate: IModifiableKpi[]) => this.onKpiUpdated(kpi, kpisGroupOrderToUpdate));
    this.$scope.$on("kpiGroupsCreated", (evt, groups: IKpiGroup[]) => this.onGroupsCreated(groups));

    this.$scope.indicators = {};
    this.$scope.scrollToCellId = "";
    this.$scope.allGroupsExpanded = true;
    this.$scope.favoriteGroupsExpanded = true;
    this.$scope.isMac = isMac();
    this.$scope.resetIndicator = this.resetIndicator;

    this.$scope.screenSettings = this.setScreenSettings(this.currentUserService.getUserSetting("kpis"));
    this.$scope.isDemoAccount = this.accountResolverService.getAccountData().type === AccountType.DemoAccount;
    this.$scope.isTrialAccount = this.accountResolverService.getAccountData().type === AccountType.TrialAccount;
    if (!this.$scope.isDemoAccount) {
      this.screenSizes.demoBannerHeight = 0;
    }
    if (!this.$scope.isTrialAccount) {
      this.screenSizes.trialBannerHeight = 0;
    }

    this.$scope.indicators.loading = { progress: true };
    this.$scope.showElements = {
      emptyState: false,
      kpiTable: false,
    };

    return this.$q
      .all({
        groups: this.kpiService.getKpiGroups(),
        isReorderKpisFeatureEnabled: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.ReorderKpis),
      })
      .then(({ groups, isReorderKpisFeatureEnabled }) => {
        this.$scope.groupsMap = this.generateGroupsMap(groups.items);
        this.isReorderKpisFeatureEnabled = isReorderKpisFeatureEnabled;
        return this.kpiService.getKpis().then((kpis) => {
          delete this.$scope.indicators.loading;

          // here we will take persisted groups(and maybe kpis) order and toggle from user or account settings
          this.orderedGroupsKpis = this.generateGroupSettings(groups.items);
          this.$scope.kpisMap = this.generateKpisMap(kpis.items);
          this.processShowElemets(this.$scope.kpisMap);
          this.sortKpisInGroups();
          this.cadences = this.generateInitialCadences();
          this.$scope.cadenceKeyMap = toKeyMap("key", this.cadences.items);
          this.$scope.favoriteGroupsWithKpis = this.fillStickyColumn(true);
          this.$scope.kpiTable = this.generateKpiTable();

          this.setTableSizes();
          this.$timeout(this.toggleStickyHeaderContent);

          return this.getSnapshots(this.generateGetSnapshotQuery()).then(() => {
            this.$timeout(this.scrollCurrentCadenceRight);
            this.onTableScrolled();
            this.onWindowResized();
            this.updateKpiTable();
            this.onNavigationToggled();
          });
        });
      })
      .catch((error) => (this.$scope.indicators.loading = { error }));
  };

  private generateGroupsMap = (groups: IKpiGroup[]): IdMap<KpiUiGroup> => {
    const groupsMap: IdMap<KpiUiGroup> = groups.reduce((map: IdMap<KpiUiGroup>, currentGroup: IKpiGroup): IdMap<KpiUiGroup> => {
      map[currentGroup.id] = {
        ...currentGroup,
        originalName: currentGroup.name,
        system: false,
        expanded: this.expandedGroupsIdSet[currentGroup.id] || false,
        favExpanded: this.favExpandedGroupsIdSet[currentGroup.id] || false,
      };

      return map;
    }, {});

    const favoriteSystemGroupIds = this.currentUserService.getUserSetting("kpiVirtualGroups")?.favorites || [];

    groupsMap[MY_KPIS_GROUP_ID] = this.systemGroup(MY_KPIS_GROUP_ID);
    groupsMap[ungroupedGroupId] = this.systemGroup(ungroupedGroupId);

    favoriteSystemGroupIds.forEach((favSystemGroupId) => {
      groupsMap[favSystemGroupId].favorite = true;
    });

    return groupsMap;
  };

  private systemGroup = (id: "mykpis" | "ungrouped"): IKpiSystemGroup => ({
    id,
    system: true,
    expanded: this.expandedGroupsIdSet[id] || false,
    favExpanded: this.favExpandedGroupsIdSet[id] || false,
    name: id,
  });

  private generateKpisMap = (kpis: IKpi[]): IdMap<IKpiItems> => {
    const groupSettingsIdMap: IdMap<IKpiGroupSetting> = toKeyMap("groupId", this.orderedGroupsKpis);

    return kpis.reduce((kpisMap: IdMap<IKpiItems>, currentKpi: IKpi): IdMap<IKpiItems> => {
      currentKpi = this.enrichKpi(currentKpi);
      this.adjustKpiGrouping(currentKpi, groupSettingsIdMap);
      kpisMap[currentKpi.id] = {
        kpi: currentKpi,
        snapshots: {},
        loadingSnapshots: true,
      };

      return kpisMap;
    }, {});
  };

  private generateKpiTable = (): IKpiTable => ({
    stickyColumn: this.fillStickyColumn(),
    columns: this.fillSnapshotColumns(),
  });

  private fillStickyColumn = (fav = false): GroupOrKpiCell[] => {
    const stickyCol: GroupOrKpiCell[] = [];

    this.orderedGroupsKpis.forEach((groupSetting) => {
      const group = this.$scope.groupsMap[groupSetting.groupId];

      if (fav && !group.favorite) {
        return;
      }

      const groupExpanded = fav ? group.favExpanded : group.expanded;

      if (!this.isSystemGroupWithoutKpis(group, groupSetting)) {
        stickyCol.push(this.groupCell(groupSetting.groupId));

        if (groupExpanded) {
          groupSetting.kpiIds.forEach((kpiId) => {
            stickyCol.push(this.kpiCell(kpiId, groupSetting.groupId));
          });
        }
      }
    });

    return stickyCol;
  };

  private fillSnapshotColumns = (): CadenceOrSnapshotCell[][] => {
    const snapshotCols: CadenceOrSnapshotCell[][] = [];

    this.cadences.items.forEach((cadence) => {
      snapshotCols.push(this.snapshotColumn(cadence));
    });

    return snapshotCols;
  };

  private snapshotColumn = (cadence: IKpiCadence): CadenceOrSnapshotCell[] => {
    const snapshotColumn: CadenceOrSnapshotCell[] = [];

    this.$scope.favoriteGroupsWithKpis.length && snapshotColumn.push(this.cadenceCell(cadence.key, favGroupsId));
    this.$scope.favoriteGroupsExpanded &&
      this.orderedGroupsKpis.forEach((groupSetting) => {
        const group = this.$scope.groupsMap[groupSetting.groupId];
        if (!group.favorite) {
          return;
        }
        if (!this.isSystemGroupWithoutKpis(group, groupSetting)) {
          snapshotColumn.push(this.cadenceCell(cadence.key, groupSetting.groupId));

          if (group.favExpanded) {
            groupSetting.kpiIds.forEach((kpiId) => {
              snapshotColumn.push(this.snapshotCell(kpiId, cadence.key, groupSetting.groupId));
            });
          }
        }
      });

    snapshotColumn.push(this.cadenceCell(cadence.key, allGroupsId));
    this.$scope.allGroupsExpanded &&
      this.orderedGroupsKpis.forEach((groupSetting) => {
        const group = this.$scope.groupsMap[groupSetting.groupId];
        if (!this.isSystemGroupWithoutKpis(group, groupSetting)) {
          snapshotColumn.push(this.cadenceCell(cadence.key, groupSetting.groupId));

          if (group.expanded) {
            groupSetting.kpiIds.forEach((kpiId) => {
              snapshotColumn.push(this.snapshotCell(kpiId, cadence.key, groupSetting.groupId));
            });
          }
        }
      });
    return snapshotColumn;
  };

  private groupCell = (groupId: string): IGroupCell => ({
    targetType: "group",
    groupId,
    id: `group-${groupId}`,
  });

  private kpiCell = (kpiId: string, groupId: string): IKpiCell => ({
    targetType: "kpi",
    kpiId,
    groupId,
    id: `kpi-${kpiId}-${groupId}`,
  });

  private cadenceCell = (cadenceKey: string, groupId: string): ICadenceCell => ({
    targetType: "cadence",
    cadenceKey,
    groupId,
    id: `cadence-${cadenceKey}-${groupId}`,
  });

  private snapshotCell = (kpiId: string, cadenceKey: string, groupId: string): ISnapshotCell => ({
    targetType: "snapshot",
    kpiId,
    cadenceKey,
    groupId,
    id: `snapshot-${kpiId}-${cadenceKey}-${groupId}`,
  });

  private isSystemGroupWithoutKpis = (group: KpiUiGroup, groupSetting: IKpiGroupSetting): boolean => group.system && !groupSetting.kpiIds.length;

  private resetIndicator = (indicator: keyof IKpisScope["indicators"]): void => {
    delete this.$scope.indicators[indicator];
  };

  private getSnapshots = (query: IGetKpiSnapshotsQueryParams): IPromise<unknown> => {
    if (!query || !query.ids) {
      this.setLoadingStatusToKpis(Object.keys(this.$scope.kpisMap), false);

      return this.$q.resolve();
    }

    this.$scope.indicators.gettingSnapshots = { progress: true };

    return this.kpiService.getKpisSnapshots(query).then(
      (snapshots) => {
        delete this.$scope.indicators.gettingSnapshots;

        this.updateFetchedKpiSnapshotsSet(query.ids);

        for (const kpiId in snapshots) {
          if (snapshots[kpiId]) {
            this.$scope.kpisMap[kpiId].loadingSnapshots = false;

            if (Array.isArray(snapshots[kpiId]) && snapshots[kpiId].length) {
              snapshots[kpiId].forEach((snapshot: IKpiSnapshot): void => {
                this.enrichSnapshot(this.$scope.kpisMap[kpiId].kpi, snapshot);

                this.$scope.kpisMap[kpiId].snapshots[snapshot.key] = {
                  snapshot: snapshot,
                  creating: false,
                };
              });
            }
          }
        }
      },
      (error) => (this.$scope.indicators.gettingSnapshots = { error })
    );
  };

  private updateFetchedKpiSnapshotsSet = (kpiIds: string): void => {
    kpiIds.split(",").forEach((kpiId: string): void => {
      this.kpiIdFetchedSnapshotsSet[kpiId] = true;
    });
  };

  private enrichKpi = (kpi: IKpi): IKpi => {
    kpi.isFormattingCustom = this.generateKpiFormattingStatus(kpi.formatting);
    kpi.unitTooltipPlacement = this.generateUnitTooltipPlacement(kpi.formatting);
    kpi.unitTooltip = this.generateKpiUnitTooltip(kpi);

    return kpi;
  };

  private generateKpiFormattingStatus = (formatting: IUnitFormatting): boolean => {
    return formatting.prefix !== "$" && formatting.prefix !== "€" && formatting.suffix !== "%";
  };

  private generateUnitTooltipPlacement = (formatting: IUnitFormatting): KpiUnitTooltipPlacement => {
    return formatting.prefix ? "left" : "right";
  };

  private generateKpiUnitTooltip = (kpi: IKpi): string => {
    return kpi.isFormattingCustom ? (kpi.formatting.prefix ? kpi.formatting.prefix : kpi.formatting.suffix ? kpi.formatting.suffix : null) : null;
  };

  private formatKpiValue = (value: number, decimalPlaces: number): string => {
    const fractionSize = decimalPlaces !== -1 ? decimalPlaces : this.getFractionSizeFromNumber(value);
    return value.toLocaleString("en", { minimumFractionDigits: fractionSize, maximumFractionDigits: fractionSize });
  };

  private getFractionSizeFromNumber = (value: number): number => {
    const decimals = value.toString().split(".")[1];
    const maxFractionSize = 3;

    if (decimals) {
      return decimals.length > maxFractionSize ? maxFractionSize : decimals.length;
    } else {
      return 0;
    }
  };

  private generateSignBeforeNumber = (value: number): "+" | "" => {
    return value > 0 ? "+" : "";
  };

  private turnValueIntoPercentage = (value: number): number => {
    const oneHundred = 100;
    const ninetyNine = 99;
    const percentage = value * oneHundred;
    const absPercentage = Math.abs(percentage);
    const negPosMultiplier: -1 | 1 = value < 0 ? -1 : 1;

    return absPercentage >= ninetyNine && absPercentage < oneHundred ? ninetyNine * negPosMultiplier : Math.round(percentage);
  };

  private setSnapshotProgressLabel = (kpi: IKpi, snapshot: IKpiSnapshot): SnapshotProgressLabel => {
    if (!snapshot.deltaAbsolute) {
      return null;
    }

    return (kpi.targetOperator === "should_increase" && snapshot.deltaAbsolute > 0) || (kpi.targetOperator === "should_decrease" && snapshot.deltaAbsolute < 0)
      ? "positive"
      : "negative";
  };

  private enrichSnapshot = (kpi: IKpi, snapshot: IKpiSnapshot): void => {
    this.enrichSnapshotValues(kpi.formatting, snapshot);
    this.colorSnapshotProgress(kpi, snapshot);
  };

  private enrichSnapshotValues = (kpiFormatting: IUnitFormatting, snapshot: IKpiSnapshot): void => {
    snapshot.deltaAbsoluteStr = !Math.abs(snapshot.deltaAbsolute)
      ? ""
      : `${this.generateSignBeforeNumber(snapshot.deltaAbsolute)}${this.formatKpiValue(snapshot.deltaAbsolute, kpiFormatting.fractionSize)}`;
    snapshot.valueStr = this.formatKpiValue(snapshot.value, kpiFormatting.fractionSize);
    snapshot.deltaPercentageStr = `${this.generateSignBeforeNumber(snapshot.deltaPercentage)}${this.turnValueIntoPercentage(snapshot.deltaPercentage)}%`;
  };

  private colorSnapshotProgress = (kpi: IKpi, snapshot: IKpiSnapshot): void => {
    snapshot.progressLabel = this.setSnapshotProgressLabel(kpi, snapshot);
  };

  private onWindowResized = (): void => {
    window.addEventListener("resize", this.windowResized);
    this.$scope.$on("$destroy", () => window.removeEventListener("resize", this.windowResized));
  };

  private windowResized = (): void => {
    // same as when scrolling - cancel timeout if another resize is done right after first
    this.$timeout.cancel(this.timeouts.resizing);
    this.timeouts.resizing = this.$timeout(() => {
      const screenWidth = window.innerWidth;
      const cadencesPerRequest = Math.max(Math.ceil(screenWidth / this.screenSizes.table.colWidth), Math.floor(minCadenceCount / 2));
      // if the screen is enlarged -> update the snapshot taken per request (if lesser)
      if (this.cadencesPerRequest < cadencesPerRequest) {
        this.cadencesPerRequest = cadencesPerRequest;
      }

      // if the user has scrolled to the end and adding columns to the table is in progress
      // wait for it to finish and then check if we have to add some more columns so the table won't have empty space in it
      if (this.extendingTable) {
        this.loadMoreSnapshotsPromise.then(() => {
          if (this.cadencesPerRequest > this.cadences.items.length) {
            this.addMoreDataToTable();
          }
        });
      } else if (this.cadencesPerRequest > this.cadences.items.length) {
        // otherwise just add some columns to the table if necessary
        this.addMoreDataToTable();
      }
    }, 1000);
    this.setTableSizes();
    this.toggleStickyHeaderContent();
  };

  private onTableScrolled = (): void => {
    document.querySelector(kpiTableSelector).addEventListener("scroll", this.tableScrolled);
    this.$scope.$on("$destroy", () => document.querySelector(kpiTableSelector).removeEventListener("scroll", this.tableScrolled));
  };

  private tableScrolled = (): void => {
    const { scrollLeft } = document.querySelector(kpiTableSelector);

    // we want to detect when scrolling is over after a certain amount of time to apply callback once
    // this is why we wrap the logic in timeout and cancel the timeout if the user is still scrolling
    this.$timeout.cancel(this.timeouts.scrolling);
    this.timeouts.scrolling = this.$timeout(() => {
      // when the user scrolls to the leftmost column (the first cadence displayed)
      // if the logic below is in progress - skip
      if (scrollLeft < this.screenSizes.table.colWidth && !this.extendingTable) {
        // else we add new columns to the screen depending on screenwidth
        this.addMoreDataToTable();
      }
    }, 50);

    // hide text of cadence cells that are in the middle of table
    this.toggleStickyHeaderContent();
  };

  private addMoreDataToTable = (): void => {
    this.extendingTable = true;
    this.loadMoreSnapshotsPromise = this.loadPreviousCadences().then(() => (this.extendingTable = false));
  };

  private loadPreviousCadences = (): IPromise<unknown> => {
    this.setLoadingStatusToKpis();
    const { groupBy } = this.$scope.screenSettings;
    const to = dayjs(this.cadences.range.from)
      .subtract(1, groupBy as dayjs.OpUnitType)
      .endOf(this.isoPeriod())
      .format(DATE_FORMAT);
    const from = dayjs(this.cadences.range.from)
      .subtract(this.cadencesPerRequest, groupBy as dayjs.OpUnitType)
      .startOf(this.isoPeriod())
      .format(DATE_FORMAT);
    const newCadences = this.generateCadences(from, to, this.cadencesPerRequest);
    const newColumns = newCadences.items.map((cadence) => this.snapshotColumn(cadence));
    this.updateCadences(newCadences);
    this.$scope.kpiTable.columns = [...newColumns, ...this.$scope.kpiTable.columns];
    document.querySelector(kpiTableSelector).scrollTo({ left: this.cadencesPerRequest * this.screenSizes.table.colWidth });

    // we need to fetch the snapshots for all the expanded KPIs in order to have their deltas displayed correctly
    return this.getSnapshots(this.generateGetSnapshotQuery());
  };

  private toggleStickyHeaderContent = (): void => {
    document.querySelectorAll(".sticky-column .details-cell.header-cell").forEach(this.toggleHeaderCellsMask);
  };

  private onNavigationToggled = (): void => {
    const navEvents = new NavigationEvents(this.$scope);
    navEvents.onNavigationToggled((args) => {
      const distanceToScroll = this.screenSizes.sideBarWidth - this.screenSizes.sideBarCollapsedWidth;
      const currentScrollLeft = document.querySelector(kpiTableSelector).scrollLeft;
      const scrollDifference = args.hideMenu ? -distanceToScroll : distanceToScroll;

      document.querySelector(kpiTableSelector).scrollTo({ left: currentScrollLeft + scrollDifference });
    });
  };

  private toggleHeaderCellsMask = (groupElement: HTMLElement): void => {
    const cadenceEls = document.querySelectorAll<HTMLElement>(`.cadence-mask.${groupElement.id}`);
    if (
      offset(groupElement).top <
      this.screenSizes.headerHeight + this.screenSizes.demoBannerHeight + this.screenSizes.trialBannerHeight + this.screenSizes.table.headerContentPadding
    ) {
      cadenceEls.forEach((el) => (el.style.visibility = "hidden"));
    } else {
      cadenceEls.forEach((el) => (el.style.visibility = "visible"));
    }
  };

  private onKpiCreated = (kpi: IKpi): void => {
    const isFirstKpi = !Object.keys(this.$scope.kpisMap).length;
    const isDynamicKpi = !!kpi.insightLink;

    // if we have no other KPIs
    if (isFirstKpi) {
      // expand My KPIs and Ungrouped groups so the new KPI is visible after creation
      this.expandedGroupsIdSet[ungroupedGroupId] = true;
      this.$scope.groupsMap[ungroupedGroupId].expanded = true;
      this.expandedGroupsIdSet[MY_KPIS_GROUP_ID] = true;
      this.$scope.groupsMap[MY_KPIS_GROUP_ID].expanded = true;
      this.persistExpandedGroupsInStorage();
    }

    kpi = this.enrichKpi(kpi);

    const groupSettingsIdMap: IdMap<IKpiGroupSetting> = toKeyMap("groupId", this.orderedGroupsKpis);
    this.adjustKpiGrouping(kpi, groupSettingsIdMap);
    // we need to add it to kpi map first so we can sort below
    this.$scope.kpisMap[kpi.id] = { kpi: kpi, snapshots: {}, loadingSnapshots: isDynamicKpi };
    const groupsToHighlight = this.getGroupsToHighlight(kpi);
    this.processShowElemets(this.$scope.kpisMap);
    this.sortKpisInGroups();
    this.updateKpiTable();

    // dynamic KPIs have initial snapshot with insight calculation value. we need to get it in order to display changes properly
    if (isDynamicKpi) {
      this.getSnapshots(this.generateGetSnapshotQuery(kpi.id));
    }

    this.setTableSizes();

    if (isFirstKpi) {
      this.$timeout(this.scrollCurrentCadenceRight);
    }
    this.$timeout(this.toggleStickyHeaderContent);

    if (groupsToHighlight.length > 0) {
      this.$timeout(() => this.highlightKpiCells(kpi.id, groupsToHighlight));
    }
  };

  private onKpiUpdated = (kpi: IKpi, kpisGroupOrderToUpdate: IModifiableKpi[]): void => {
    const oldKpiItems: IKpiItems = angular.copy(this.$scope.kpisMap[kpi.id]);

    kpi = this.enrichKpi(kpi);
    const groupSettingsIdMap: IdMap<IKpiGroupSetting> = toKeyMap("groupId", this.orderedGroupsKpis);
    this.adjustKpiGrouping(kpi, groupSettingsIdMap);
    // we need to update kpi in kpis map first so we can sort below
    angular.extend(this.$scope.kpisMap[kpi.id].kpi, kpi);
    const groupsToHighlight = this.getGroupsToHighlight(kpi, oldKpiItems.kpi);
    this.handleKpisGroupOrderToUpdate(kpisGroupOrderToUpdate);
    this.sortKpisInGroups();
    this.updateKpiTable();
    this.setTableSizes();
    this.$timeout(this.toggleStickyHeaderContent);
    this.enrichSnapshotsIfNeeded(oldKpiItems, kpi);
    this.getSnapshotsIfAggregationIsChanged(oldKpiItems, kpi);

    if (groupsToHighlight.length > 0) {
      this.$timeout(() => this.highlightKpiCells(kpi.id, groupsToHighlight));
    }
  };

  private getGroupsToHighlight = (kpi: IKpi, oldKpi?: IKpi) => {
    const groupsToHighlight: string[] = [];

    if (oldKpi && kpi.groups.length > 0) {
      groupsToHighlight.push(...kpi.groups.filter((updatedKpiGroup) => !oldKpi.groups.includes(updatedKpiGroup)));
    } else if (kpi.groups.length > 0) {
      groupsToHighlight.push(...kpi.groups);
    } else {
      groupsToHighlight.push(ungroupedGroupId);
    }

    if (this.shouldBeAddedToMyKpis(this.$scope.kpisMap[kpi.id].kpi.ownerIds, getCurrentUserId())) {
      groupsToHighlight.push(MY_KPIS_GROUP_ID);
    }

    return groupsToHighlight;
  };

  private highlightKpiCells(kpiId: string, groupsToHighlight: string[]) {
    const selectorToHighlight: HTMLElement[] = [];

    groupsToHighlight.forEach((groupToHighlight) => {
      this.pushSelectorsToHighlight("details-fav-cell-kpi", kpiId, groupToHighlight, selectorToHighlight);
      this.pushSelectorsToHighlight("details-cell-kpi", kpiId, groupToHighlight, selectorToHighlight);
    });

    selectorToHighlight.forEach((element) => {
      element.classList.add("highlight-cell");
    });

    this.$timeout(
      () =>
        selectorToHighlight.forEach((element) => {
          element.classList.remove("highlight-cell");
        }),
      10000
    );
  }

  private pushSelectorsToHighlight = (selector: string, kpiId: string, groupToHighlight: string, selectorToHighlight: HTMLElement[]) => {
    const element = document.querySelector<HTMLElement>(`.${selector}-${kpiId}-${groupToHighlight}`);
    if (element) {
      selectorToHighlight.push(element);
    }
  };

  private handleKpisGroupOrderToUpdate = (kpisWithGroupOrderToUpdate: IModifiableKpi[]) => {
    if (kpisWithGroupOrderToUpdate && kpisWithGroupOrderToUpdate.length > 0) {
      kpisWithGroupOrderToUpdate.forEach((kpiWithGroupOrderToUpdate) => {
        this.$scope.kpisMap[kpiWithGroupOrderToUpdate.id].kpi.groupsOrder = kpiWithGroupOrderToUpdate.groupsOrder;
      });
    }
  };

  private onGroupsCreated = (groups: IKpiGroup[]) => {
    groups.forEach((group) => {
      const newGroupSetting: IKpiGroupSetting = { groupId: group.id, kpiIds: [] };
      this.$scope.groupsMap[group.id] = { id: group.id, name: group.name, system: false, expanded: true, favExpanded: false, originalName: group.name };
      this.expandedGroupsIdSet[group.id] = true;
      const ungroupedSetting = this.orderedGroupsKpis.pop();
      this.orderedGroupsKpis.push(newGroupSetting, ungroupedSetting);
    });

    this.persistExpandedGroupsInStorage();
    this.sortKpiGroupSettings();
    this.updateKpiTable();
  };

  private openCreateSnapshotForm = (kpiId: string, cadence: string, groupId: string): void => {
    if (!this.hasManageDataPermission() || !cadence || this.$scope.kpisMap[kpiId].loadingSnapshots) {
      return;
    }

    if (this.openedSnapshot) {
      this.openedSnapshot.creating = false;
    }

    this.$scope.newSnapshot = this.generateNewSnapshot();

    if (!this.$scope.kpisMap[kpiId].snapshots[cadence]) {
      this.$scope.kpisMap[kpiId].snapshots[cadence] = {
        snapshot: null,
        creating: true,
      };
    } else {
      this.$scope.kpisMap[kpiId].snapshots[cadence].creating = true;
    }

    this.openedSnapshot = this.$scope.kpisMap[kpiId].snapshots[cadence];

    setTimeout(() => document.getElementById(`${groupId}-snapshot`).focus());
  };

  // called when close button is mouse down
  // this event triggers before blur and blur is not triggered at all
  private closeCreateSnapshotForm = (kpiId: string, cadenceKey: string): void => {
    this.$scope.newSnapshot = null;
    this.openedSnapshot = null;
    this.$scope.kpisMap[kpiId].snapshots[cadenceKey].creating = false;
  };

  private enrichSnapshotsIfNeeded = (kpiItems: IKpiItems, kpi: IKpi): void => {
    const isTargetOperatorChanged = kpiItems.kpi.targetOperator !== kpi.targetOperator;
    const isFractionSizeChanged = kpiItems.kpi.formatting.fractionSize !== kpi.formatting.fractionSize;

    // doing them together to avoid repeating for in and because both are sync operations
    if (isTargetOperatorChanged || isFractionSizeChanged) {
      for (const cadenceKey in this.$scope.kpisMap[kpi.id].snapshots) {
        if (this.$scope.kpisMap[kpi.id].snapshots[cadenceKey]) {
          const snapshot: IKpiSnapshot = this.$scope.kpisMap[kpi.id].snapshots[cadenceKey].snapshot;

          if (isTargetOperatorChanged) {
            this.colorSnapshotProgress(kpi, snapshot);
          }

          if (isFractionSizeChanged) {
            this.enrichSnapshotValues(kpi.formatting, snapshot);
          }
        }
      }
    }
  };

  private getSnapshotsIfAggregationIsChanged = (kpiItems: IKpiItems, kpi: IKpi): void => {
    if (kpiItems.kpi.aggregation !== kpi.aggregation) {
      this.setLoadingStatusToKpis([kpi.id]);
      this.getSnapshots(this.generateGetSnapshotQuery(kpi.id));
    }
  };

  private validateSnapshotInput = (inputValue: string): void => {
    this.$scope.newSnapshot.error = isNaN(+inputValue) || !inputValue ? "please_enter_number" : "";
  };

  private onSnapshotBlur = (evt: JQueryEventObject, kpiId: string, cadenceKey: string): void => {
    // clicking outside snapshot cell with check if input is valid and save is so
    // if invalid it will just close the form
    if (!this.isNewSnapshotValid()) {
      this.closeCreateSnapshotForm(kpiId, cadenceKey);

      return;
    }

    this.createSnapshot(kpiId, cadenceKey);
  };

  private onSnapshotKeydown = ($event: JQueryEventObject, kpiId: string, cadenceKey: string, groupId: string): void => {
    const isEsc: boolean = $event.keyCode === 27;
    const isTab: boolean = $event.keyCode === 9;
    const isEnter: boolean = $event.keyCode === 13;

    if (isEsc) {
      this.closeCreateSnapshotForm(kpiId, cadenceKey);

      return;
    }

    if (isEnter || isTab) {
      $event.preventDefault();

      // if input value is invalid - keep form open but do not call createSnapshot method
      if (!this.isNewSnapshotValid()) {
        return;
      }

      this.createSnapshot(kpiId, cadenceKey, groupId, isTab);
    }
  };

  private onGroupBlur = ($event: JQueryEventObject, groupId: string): void => {
    if (groupId === newGroupId) {
      this.removeGroupFromCollections(groupId);
      this.setTableSizes();
      this.toggleStickyHeaderContent();

      return;
    }

    const group = <IKpiExistingUiGroup>this.$scope.groupsMap[groupId];
    group.name = group.name.trim();

    if (!group.name) {
      group.name = group.originalName;

      return;
    }

    if (group.name === group.originalName) {
      return;
    }

    this.patchKpiGroup($event, groupId);
  };

  private onGroupKeydown = ($event: JQueryEventObject, groupId: string): void => {
    if (groupId === newGroupId) {
      this.createNewGroup($event, groupId);

      return;
    }

    this.editExistingGroup($event, groupId);
  };

  private editExistingGroup = ($event: JQueryEventObject, groupId: string): void => {
    const isEsc: boolean = $event.keyCode === 27;
    const isEnter: boolean = $event.keyCode === 13;

    const existingGroup = <IKpiExistingUiGroup>this.$scope.groupsMap[groupId];
    if (isEsc) {
      existingGroup.name = existingGroup.originalName;
      setTimeout(() => ($event.target as HTMLElement).blur());

      return;
    }

    if (isEnter) {
      $event.preventDefault();

      existingGroup.name = existingGroup.name.trim();

      if (!existingGroup.name) {
        existingGroup.name = existingGroup.originalName;
        setTimeout(() => ($event.target as HTMLElement).blur());

        return;
      }

      if (existingGroup.name === existingGroup.originalName) {
        setTimeout(() => ($event.target as HTMLElement).blur());

        return;
      }

      this.patchKpiGroup($event, groupId);
    }
  };

  private patchKpiGroup = ($event: JQueryEventObject, groupId: string): void => {
    this.$scope.indicators.patchingGroup = { progress: true };
    const patchedGroup = <IKpiExistingUiGroup>this.$scope.groupsMap[groupId];

    this.kpiService.patchKpiGroup(groupId, { name: patchedGroup.name }).then(
      () => {
        patchedGroup.originalName = patchedGroup.name;
        if ($event.type !== "blur") {
          setTimeout(() => ($event.target as HTMLElement).blur());
        }
        this.sortCollectionsAndUpdateTableIfNeeded();
        this.toggleStickyHeaderContent();
        delete this.$scope.indicators.patchingGroup;
      },
      (error) => {
        // Set the original name of the group on error
        patchedGroup.name = patchedGroup.originalName;
        this.$scope.indicators.patchingGroup = { error };
      }
    );
  };

  private createNewGroup = ($event: JQueryEventObject, groupId: "newgroup"): void => {
    const isEsc: boolean = $event.keyCode === 27;
    const isEnter: boolean = $event.keyCode === 13;

    if (isEsc) {
      this.removeGroupFromCollections(groupId);
      this.setTableSizes();
      this.toggleStickyHeaderContent();

      return;
    }

    if (isEnter) {
      $event.preventDefault();

      this.$scope.groupsMap[groupId].name = this.$scope.groupsMap[groupId].name.trim();

      if (!this.$scope.groupsMap[groupId].name) {
        return;
      }

      this.$scope.indicators.creatingGroup = { progress: true };

      this.kpiService.createKpiGroup({ name: this.$scope.groupsMap[groupId].name }).then(
        (group: IKpiGroup) => {
          this.convertNewGroupToExisting(groupId, group);
          this.expandedGroupsIdSet[group.id] = true;
          this.persistExpandedGroupsInStorage();
          this.$timeout(() => ($event.target as HTMLElement).blur());
          delete this.$scope.indicators.creatingGroup;
        },
        (error) => (this.$scope.indicators.creatingGroup = { error })
      );
    }
  };

  private convertNewGroupToExisting = (newGroupId: "newgroup", createdGroup: IKpiGroup): void => {
    delete this.$scope.groupsMap[newGroupId];
    this.$scope.groupsMap[createdGroup.id] = { ...createdGroup, originalName: createdGroup.name, system: false, expanded: true, favExpanded: false };
    this.orderedGroupsKpis.find((groupSetting) => groupSetting.groupId == newGroupId).groupId = createdGroup.id;
    angular.extend(
      this.$scope.kpiTable.stickyColumn.find((cell) => cell.groupId === newGroupId),
      this.groupCell(createdGroup.id)
    );
    this.$scope.kpiTable.columns.forEach((column) =>
      angular.extend(
        column.find((cell) => cell.groupId === newGroupId),
        this.cadenceCell(column[0].cadenceKey, createdGroup.id)
      )
    );
  };

  private sortCollectionsAndUpdateTableIfNeeded = (): void => {
    const previousOrder = angular.copy(this.orderedGroupsKpis);
    this.sortKpiGroupSettings();

    // sort kpiIds in group settings
    this.sortKpisInGroups();

    // if there are changes in order -> sort the kpi table
    if (!angular.equals(previousOrder, this.orderedGroupsKpis)) {
      this.updateKpiTable();
    }
  };

  private sortKpiGroupSettings() {
    const ungrouppedSetting = this.orderedGroupsKpis.pop();
    const myKpisSetting = this.orderedGroupsKpis.shift();

    // sort group settings
    this.orderedGroupsKpis = [
      myKpisSetting,
      ...this.orderedGroupsKpis.sort((a, b) => this.$scope.groupsMap[a.groupId].name.toLowerCase().localeCompare(this.$scope.groupsMap[b.groupId].name.toLowerCase())),
      ungrouppedSetting,
    ];
  }

  private updateKpiTable = (): void => {
    this.$scope.kpiTable.stickyColumn = this.fillStickyColumn();
    this.$scope.favoriteGroupsWithKpis = this.fillStickyColumn(true);
    this.$scope.kpiTable.columns = this.fillSnapshotColumns();
  };

  private createSnapshot = (kpiId: string, cadenceKey: string, groupId: string = null, tabIsHit = false): void => {
    const payload: ICreateKpiSnapshotPayload = {
      value: +this.$scope.newSnapshot.value,
      group: cadenceKey,
      timeZone: this.timeZone,
    };

    this.$scope.indicators.creatingSnapshot = { progress: true };

    this.kpiService.createKpiSnapshot(kpiId, payload).then(
      () => {
        delete this.$scope.indicators.creatingSnapshot;

        let kpiIds: string[];
        const cadenceRange = dateRangeForCadence(cadenceKey);
        const isSnapshotCreatedInFirstColumn = cadenceRange.from === this.cadences.range.from;
        const isSnapshotCreatedInLastColumn = cadenceRange.to === this.cadences.range.to;

        if (isSnapshotCreatedInFirstColumn) {
          // if we're creating a snapshot in the first cadence -> add a column before it
          const from = dayjs(this.cadences.range.from)
            .subtract(1, this.$scope.screenSettings.groupBy as dayjs.OpUnitType)
            .startOf(this.isoPeriod())
            .format(DATE_FORMAT);
          const to = dayjs(from).endOf(this.isoPeriod()).format(DATE_FORMAT);
          const newCadences = this.generateCadences(from, to, 1);
          this.$scope.kpiTable.columns.unshift(this.snapshotColumn(newCadences.items[0]));
          this.updateCadences(newCadences);
          // we need to get snapshots for all the expanded KPIs because there might be snapshots in the cadence added
          kpiIds = this.expandedKpisIds();
        } else if (isSnapshotCreatedInLastColumn) {
          // if we're creating a snapshot in the last cadence -> add a column after it
          const to = dayjs(this.cadences.range.to)
            .add(1, this.$scope.screenSettings.groupBy as dayjs.OpUnitType)
            .endOf(this.isoPeriod())
            .format(DATE_FORMAT);
          const from = dayjs(to).startOf(this.isoPeriod()).format(DATE_FORMAT);
          const newCadences = this.generateCadences(from, to, 1);
          this.$scope.kpiTable.columns.push(this.snapshotColumn(newCadences.items[0]));
          this.updateCadences(newCadences);
          // we need to get snapshots for all the expanded KPIs because there might be snapshots in the cadence added
          kpiIds = this.expandedKpisIds();
        } else {
          kpiIds = [kpiId];
        }

        this.setLoadingStatusToKpis(kpiIds);
        // we need to fetch snapshots for all the cadences to have deltas correctly calculated
        this.getSnapshots(this.generateGetSnapshotQuery(kpiIds.join(","))).then(() => {
          if (tabIsHit) {
            const nextCadenceKey = dayjs(cadenceRange.to)
              .add(1, this.$scope.screenSettings.groupBy as dayjs.OpUnitType)
              .format(dateFormatsMap[this.$scope.screenSettings.groupBy].key);

            if (isSnapshotCreatedInLastColumn || isSnapshotCreatedInFirstColumn) {
              this.$timeout(() => {
                const kpiTableEl = document.querySelector<HTMLElement>(kpiTableSelector);
                kpiTableEl.scrollTo({ left: kpiTableEl.scrollLeft + this.screenSizes.table.colWidth });
                this.openCreateSnapshotForm(kpiId, nextCadenceKey, groupId);
              });
            } else {
              this.openCreateSnapshotForm(kpiId, nextCadenceKey, groupId);
            }
          }
        });
      },
      (error) => (this.$scope.indicators.creatingSnapshot = { error })
    );
  };

  private isNewSnapshotValid = (): boolean => this.$scope.newSnapshot && this.$scope.newSnapshot.value && !this.$scope.newSnapshot.error;

  private convertStrArrToDatesArr = (strArr: string[]): dayjs.Dayjs[] => {
    return strArr.map((str) => dayjs(str));
  };

  private generateInitialCadences = (): IKpiCadenceData => {
    const { groupBy } = this.$scope.screenSettings;
    const screenWidth = window.innerWidth;
    this.cadencesPerRequest = Math.max(Math.ceil(screenWidth / this.screenSizes.table.colWidth), Math.floor(minCadenceCount / 2));
    const to = dayjs()
      .add(futureCadencesDisplayedInitially, groupBy as dayjs.OpUnitType)
      .endOf(this.isoPeriod())
      .format(DATE_FORMAT);
    const from = dayjs()
      .subtract(this.cadencesPerRequest, groupBy as dayjs.OpUnitType)
      .startOf(this.isoPeriod())
      .format(DATE_FORMAT);
    const duration: number = Math.max(1 + this.cadencesPerRequest + futureCadencesDisplayedInitially, minCadenceCount);

    return this.generateCadences(from, to, duration);
  };

  private generateCadences = (from: string, to: string, duration: number): IKpiCadenceData => ({
    items: this.generateCadenceItems(from, duration),
    range: {
      from,
      to,
    },
  });

  private generateCadenceItems = (from: string, duration: number): IKpiCadence[] => {
    const { groupBy } = this.$scope.screenSettings;
    const currentCadenceKey = dayjs().format(dateFormatsMap[groupBy].key);

    return new Array(duration).fill(null).map((num, index) => {
      const calculationKey = dayjs(from).add(index, groupBy as dayjs.OpUnitType);
      const cadenceKey = calculationKey.format(dateFormatsMap[groupBy].key);

      return {
        key: cadenceKey,
        viewVal: calculationKey.format(dateFormatsMap[groupBy].viewVal),
        isCurrent: cadenceKey === currentCadenceKey,
        range: generateSnapshotRange(groupBy, calculationKey),
      };
    });
  };

  private updateCadences = (newCadences: IKpiCadenceData): void => {
    const fromDates = this.convertStrArrToDatesArr([newCadences.range.from, this.cadences.range.from]);
    const toDates = this.convertStrArrToDatesArr([newCadences.range.to, this.cadences.range.to]);
    const areCadencesAddedInTheFuture = newCadences.range.from === this.cadences.range.from;

    this.cadences = {
      items: areCadencesAddedInTheFuture ? [...this.cadences.items, ...newCadences.items] : [...newCadences.items, ...this.cadences.items],
      range: {
        from: dayjs.min(fromDates).format(DATE_FORMAT),
        to: dayjs.max(toDates).format(DATE_FORMAT),
      },
    };
    this.$scope.cadenceKeyMap = { ...toKeyMap("key", newCadences.items), ...this.$scope.cadenceKeyMap };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isoPeriod = (): any => (this.$scope.screenSettings.groupBy === "week" ? "isoWeek" : this.$scope.screenSettings.groupBy);

  private setScreenSettings = (userSettings: IKpisScreenSettings): IKpisScreenSettings => (userSettings ? userSettings : this.generateDefaultScreenSettings());

  private generateDefaultScreenSettings = (): IKpisScreenSettings => ({ groupBy: "day" });

  private generateGroupSettings = (groups: IKpiGroup[]): IKpiGroupSetting[] => {
    // these group settings will be passed as param later and will be taken from user/account settings
    // groups iteration below will involve only the groups that are not persisted(newly created)
    const groupSettings: IKpiGroupSetting[] = [];

    // my kpis group always appear first
    groupSettings.push(this.generateGroupSetting(MY_KPIS_GROUP_ID));

    groups.sort(byTextField("name")).forEach((group: IKpiGroup): void => {
      groupSettings.push(this.generateGroupSetting(group.id));
    });

    // ungrouped group always appear last
    groupSettings.push(this.generateGroupSetting(ungroupedGroupId));

    return groupSettings;
  };

  private generateGroupSetting = (groupId: string): IKpiGroupSetting => ({ groupId, kpiIds: [] });

  private adjustKpiGrouping = (kpi: IKpi, groupSettingsIdMap: IdMap<IKpiGroupSetting> = toKeyMap("groupId", this.orderedGroupsKpis)): void => {
    this.orderedGroupsKpis.forEach((groupSetting: IKpiGroupSetting): void => {
      let shouldBeAdded: boolean;

      switch (groupSetting.groupId) {
        case MY_KPIS_GROUP_ID: {
          const ownerIds = kpi.ownerIds;
          shouldBeAdded = this.shouldBeAddedToMyKpis(ownerIds, getCurrentUserId());
          break;
        }
        case ungroupedGroupId:
          shouldBeAdded = this.isKpiUngrouped(kpi.groups.length);
          break;

        default:
          shouldBeAdded = this.shouldBeAddedToCustomGroup(groupSetting.groupId, kpi.groups);
          break;
      }

      this.adjustGroupKpis(groupSetting.groupId, kpi.id, shouldBeAdded, groupSettingsIdMap);
    });
  };

  private adjustGroupKpis = (groupId: string, kpiId: string, shouldBeAddedToGroup: boolean, groupSettingsIdMap: IdMap<IKpiGroupSetting>): void => {
    const isKpiAlreadyAdded: boolean = this.isKpiAlreadyAddedToGroup(kpiId, groupSettingsIdMap[groupId].kpiIds);

    if (!isKpiAlreadyAdded && shouldBeAddedToGroup) {
      groupSettingsIdMap[groupId].kpiIds.push(kpiId);

      return;
    }

    if (isKpiAlreadyAdded && !shouldBeAddedToGroup) {
      groupSettingsIdMap[groupId].kpiIds = this.removeIdFromIdsCollection(kpiId, groupSettingsIdMap[groupId].kpiIds);
    }
  };

  private removeIdFromIdsCollection = (idToRemove: string, ids: string[]): string[] => ids.filter((id) => id !== idToRemove);

  private shouldBeAddedToMyKpis = (ownerIds: string[], currentUserId: string = getCurrentUserId()): boolean => !!ownerIds.find((oid) => oid === currentUserId);

  private isKpiUngrouped = (groupsCount: number): boolean => !groupsCount;

  private shouldBeAddedToCustomGroup = (groupId: string, kpiGroups: string[]): boolean => kpiGroups.includes(groupId);

  private isKpiAlreadyAddedToGroup = (kpiId: string, groupKpiIds: string[]): boolean => groupKpiIds.includes(kpiId);

  private sortKpisInGroups = (): void => {
    if (this.isReorderKpisFeatureEnabled) {
      this.orderedGroupsKpis.forEach((groupSetting: IKpiGroupSetting): void => {
        const myKpiOrder = this.currentUserService.getUserSetting("myKpiOrder");
        const kpisList = Object.values(this.$scope.kpisMap).map((values) => values.kpi);
        const kpisForGroupOrder = kpisList.filter(
          (kpi) =>
            kpi.groupsOrder &&
            Object.keys(kpi.groupsOrder).includes(groupSetting.groupId) &&
            (groupSetting.groupId === ungroupedGroupId || kpi.groups.includes(groupSetting.groupId))
        );

        if (kpisForGroupOrder.length > 0) {
          groupSetting.kpiIds = groupSetting.kpiIds.sort(
            (a, b) => this.$scope.kpisMap[a].kpi.groupsOrder[groupSetting.groupId] - this.$scope.kpisMap[b].kpi.groupsOrder[groupSetting.groupId]
          );
        } else if (groupSetting.groupId === MY_KPIS_GROUP_ID && myKpiOrder) {
          groupSetting.kpiIds = myKpiOrder;
        } else {
          groupSetting.kpiIds = groupSetting.kpiIds.sort((x, y) =>
            this.$scope.kpisMap[x].kpi.name.toLowerCase().localeCompare(this.$scope.kpisMap[y].kpi.name.toLowerCase())
          );
        }
      });
    } else {
      this.orderedGroupsKpis.forEach((groupSetting: IKpiGroupSetting): void => {
        groupSetting.kpiIds = groupSetting.kpiIds.sort((x, y) =>
          this.$scope.kpisMap[x].kpi.name.toLowerCase().localeCompare(this.$scope.kpisMap[y].kpi.name.toLowerCase())
        );
      });
    }
  };

  private processShowElemets(kpisMap: IdMap<IKpiItems>): void {
    const kpisMapLength = Object.keys(kpisMap).length;

    this.$scope.showElements = {
      kpiTable: !this.$scope.indicators.loading && kpisMapLength > 0,
      emptyState: !this.$scope.indicators.loading && kpisMapLength === 0,
    };
  }

  private toggleGroup = (group: KpiUiGroup, fromFavorites = false): void => {
    if (fromFavorites) {
      switch (!!group.favExpanded) {
        case true:
          group.favExpanded = false;
          delete this.favExpandedGroupsIdSet[group.id];
          this.updateKpiTable();
          break;
        case false:
          group.favExpanded = true;
          this.favExpandedGroupsIdSet[group.id] = true;
          this.updateKpiTable();
          this.getToggledGroupSnapshotsIfNeeded(group.id);
          break;
      }
    } else {
      switch (!!group.expanded) {
        case true:
          group.expanded = false;
          delete this.expandedGroupsIdSet[group.id];
          this.updateKpiTable();
          break;
        case false:
          group.expanded = true;
          this.expandedGroupsIdSet[group.id] = true;
          this.updateKpiTable();
          this.getToggledGroupSnapshotsIfNeeded(group.id);
          break;
      }
    }
    this.setTableSizes();
    this.$timeout(this.toggleStickyHeaderContent);
    this.persistExpandedGroupsInStorage();
    if ((!fromFavorites && group.expanded) || (fromFavorites && group.favExpanded)) {
      this.viewHistoryService.addItemToViewHistory(
        this.viewHistoryService.payloadBuilder.withKpiGroup(group).forSource("navigation").generate(),
        navItemsFactory.withRandomColor(navItemsFactory.buildKpiGroupNavItem(group))
      );
    }
  };

  private getToggledGroupSnapshotsIfNeeded = (groupId: string): void => {
    const kpiIdsToGetSnapshotsFor: string = this.orderedGroupsKpis
      .find((groupSetting): boolean => groupSetting.groupId === groupId)
      .kpiIds.reduce((kpiIdsArr: string[], currentKpiId: string): string[] => {
        if (!this.kpiIdFetchedSnapshotsSet[currentKpiId]) {
          kpiIdsArr.push(currentKpiId);
        }

        return kpiIdsArr;
      }, [])
      .join(",");

    if (kpiIdsToGetSnapshotsFor) {
      this.getSnapshots(this.generateGetSnapshotQuery(kpiIdsToGetSnapshotsFor));
    }
  };

  private calculateTableSizes = (): IKpisTableDimensions => {
    let tableHeight = this.screenSizes.allGroupsHeader;

    if (this.$scope.allGroupsExpanded) {
      tableHeight = this.orderedGroupsKpis.reduce((height: number, groupSetting: IKpiGroupSetting): number => {
        // if My KPIs and/or Ungrouped do not contain any KPIs - do not take into consideration as they will be hidden
        // else if collapsed increment hight with group details row height
        // else add group details row height and sum of KPIs row height
        if (this.$scope.groupsMap[groupSetting.groupId].system && !groupSetting.kpiIds.length) {
          return height;
        }

        height += !this.$scope.groupsMap[groupSetting.groupId].expanded
          ? this.screenSizes.table.headerHeight
          : this.screenSizes.table.headerHeight + this.screenSizes.table.rowHeight * groupSetting.kpiIds.length;

        return height;
      }, tableHeight);
    }

    if (this.$scope.favoriteGroupsWithKpis.length) {
      tableHeight += this.screenSizes.table.headerHeight;
      this.$scope.favoriteGroupsExpanded &&
        this.$scope.favoriteGroupsWithKpis.forEach((favGroup) => {
          if (favGroup.targetType === "group") {
            tableHeight += this.screenSizes.table.headerHeight;
          } else if (favGroup.targetType === "kpi") {
            tableHeight += this.screenSizes.table.rowHeight;
          }
        });
    }

    const bottom = Math.max(
      window.innerHeight -
        tableHeight -
        this.screenSizes.table.paddingBottom -
        this.screenSizes.defaultTableTop -
        this.screenSizes.demoBannerHeight -
        this.screenSizes.trialBannerHeight,

      this.screenSizes.table.offsetBottom
    );

    return {
      bottom,
      borderBottom: bottom + this.screenSizes.table.paddingBottom,
    };
  };

  private setTableSizes = (): void => {
    this.$scope.tableSizes = this.calculateTableSizes();
  };

  private scrollCurrentCadenceRight = (): void => {
    const tableWidth = document.querySelector(kpiTableSelector).clientWidth;
    const scrollDistance =
      (this.cadences.items.length - futureCadencesDisplayedInitially) * this.screenSizes.table.colWidth -
      (tableWidth - this.screenSizes.table.leftColWidth - (this.screenSizes.table.paddingRight - this.screenSizes.table.borderWidth));

    document.querySelector(kpiTableSelector).scrollTo({ left: scrollDistance });
  };

  private generateGetSnapshotQuery = (ids = this.expandedKpisIds().join(",")): IGetKpiSnapshotsQueryParams => {
    return {
      ids: ids,
      timeZone: this.timeZone,
      groupBy: this.$scope.screenSettings.groupBy,
      from: this.cadences.range.from,
      to: this.cadences.range.to,
    };
  };

  private expandedKpisIds = (): string[] => {
    return Object.keys(
      this.orderedGroupsKpis
        .filter((groupSetting: IKpiGroupSetting): boolean => {
          return this.$scope.groupsMap[groupSetting.groupId].expanded || this.$scope.groupsMap[groupSetting.groupId].favExpanded;
        })
        .reduce((kpiIdSet: IdMap<boolean>, currentSetting: IKpiGroupSetting): IdMap<boolean> => {
          currentSetting.kpiIds.forEach((kpiId: string): void => {
            kpiIdSet[kpiId] = true;
          });

          return kpiIdSet;
        }, {})
    );
  };

  private setLoadingStatusToKpis = (idsArr: string[] = Object.keys(this.$scope.kpisMap), status = true): void => {
    idsArr.forEach((kpiId: string): void => {
      this.$scope.kpisMap[kpiId].loadingSnapshots = status;
    });
  };

  private onScreenSettingsChanged = (): void => {
    this.cadences = this.generateInitialCadences();
    this.kpiIdFetchedSnapshotsSet = {};
    this.setLoadingStatusToKpis();
    this.$scope.cadenceKeyMap = toKeyMap("key", this.cadences.items);
    this.$scope.kpiTable.columns = this.fillSnapshotColumns();
    this.$timeout(this.toggleStickyHeaderContent);
    this.getSnapshots(this.generateGetSnapshotQuery()).then(() => {
      this.scrollCurrentCadenceRight();
      this.setUserKpiSettings(this.$scope.screenSettings);
    });
  };

  private openKpiForm = (kpi?: IKpi) => {
    if (!this.hasManageDataPermission()) {
      return;
    }

    if (kpi) {
      this.$state.go("._singleKpi.form", { kpiId: kpi.id });
    } else {
      this.$state.go("._singleKpi.new");
    }
  };

  private beginNewGroup = (): void => {
    if (!this.hasManageDataPermission()) {
      return;
    }

    const distanceToScroll = this.expandedGroupsIdSet[MY_KPIS_GROUP_ID] ? this.orderedGroupsKpis[0].kpiIds.length * this.screenSizes.table.rowHeight : 0;

    if (this.$scope.groupsMap[newGroupId]) {
      this.$timeout(() => {
        document.querySelector(kpiTableSelector).scrollTo({ top: distanceToScroll });
        document.getElementById(`group-${newGroupId}-input`).focus();
      });
      return;
    }

    const newGroupSetting: IKpiGroupSetting = { groupId: newGroupId, kpiIds: [] };
    const newGroup: IKpiNewGroup = { id: newGroupId, name: "", system: false, expanded: true, favExpanded: false };

    this.$scope.groupsMap[newGroupId] = newGroup;
    this.addNewGroupToTable(newGroupSetting);
    this.$timeout(() => {
      document.querySelector(kpiTableSelector).scrollTo({ top: distanceToScroll });
      document.getElementById(`group-${newGroupId}-input`).focus();
    });
    this.setTableSizes();
    this.toggleStickyHeaderContent();
  };

  private addNewGroupToTable = (newGroupSetting: IKpiGroupSetting): void => {
    const ungrouppedSetting = this.orderedGroupsKpis.pop();
    const myKpisSetting = this.orderedGroupsKpis.shift();

    this.orderedGroupsKpis = [myKpisSetting, newGroupSetting, ...this.orderedGroupsKpis, ungrouppedSetting];
    this.updateKpiTable();
  };

  private openConfirmDeleteGroupModal = (group: IKpiGroup): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.confirmDeleteGroupModal = this.$uibModal.open({
      templateUrl: require("../views/confirm-delete-group.jade"),
      windowClass: "medium-small",
      backdropClass: "dark-backdrop",
      backdrop: true,
      controller: [
        "$scope",
        function ($scope) {
          $scope.group = group;
          $scope.indicators = ctrl.$scope.indicators;
          $scope.deleteGroup = ctrl.deleteGroup;
        },
      ],
    });
  };

  private deleteGroup = (groupId: string): void => {
    this.$scope.indicators.deletingGroup = { progress: true };

    this.kpiService.deleteKpiGroup(groupId).then(
      () => {
        delete this.$scope.indicators.deletingGroup;
        this.confirmDeleteGroupModal.close();

        const groupSettingsIdMap: IdMap<IKpiGroupSetting> = toKeyMap("groupId", this.orderedGroupsKpis);

        if (this.isReorderKpisFeatureEnabled) {
          const patchableKpis: IModifiableKpi[] = [];
          const ungroupedKpis = Object.values(this.$scope.kpisMap)
            .map((values) => values.kpi)
            .filter((kpi) => kpi.groups.length === 0);

          for (let i = 0; i < groupSettingsIdMap[groupId].kpiIds.length; i++) {
            const kpiId = groupSettingsIdMap[groupId].kpiIds[i];
            this.$scope.kpisMap[kpiId].kpi.groups = this.$scope.kpisMap[kpiId].kpi.groups.filter((kpiGroupId: string) => kpiGroupId !== groupId);

            if (ungroupedKpis.length > 0 && ungroupedKpis[0].groupsOrder && ungroupedKpis[0].groupsOrder[ungroupedGroupId] !== undefined) {
              this.$scope.kpisMap[kpiId].kpi.groupsOrder = this.$scope.kpisMap[kpiId].kpi.groupsOrder || {};
              this.$scope.kpisMap[kpiId].kpi.groupsOrder[ungroupedGroupId] = ungroupedKpis.length + i;
            }

            if (this.$scope.kpisMap[kpiId].kpi.groupsOrder && this.$scope.kpisMap[kpiId].kpi.groupsOrder[groupId] !== undefined) {
              delete this.$scope.kpisMap[kpiId].kpi.groupsOrder[groupId];
            }

            if (this.$scope.kpisMap[kpiId].kpi.groupsOrder) {
              patchableKpis.push({ id: this.$scope.kpisMap[kpiId].kpi.id, groupsOrder: this.$scope.kpisMap[kpiId].kpi.groupsOrder } as IModifiableKpi);
            }
          }

          groupSettingsIdMap[groupId].kpiIds.forEach((kpiId) => this.adjustKpiGrouping(this.$scope.kpisMap[kpiId].kpi, groupSettingsIdMap));

          if (patchableKpis.length > 0) {
            this.kpiService.patchKpis(toKeyMap("id", patchableKpis)).then(() => {
              this.updateTableAfterGroupRemoval(groupId);
            });
          } else {
            this.updateTableAfterGroupRemoval(groupId);
          }
        } else {
          groupSettingsIdMap[groupId].kpiIds.forEach((kpiId: string): void => {
            this.$scope.kpisMap[kpiId].kpi.groups = this.$scope.kpisMap[kpiId].kpi.groups.filter((kpiGroupId: string) => kpiGroupId !== groupId);
            this.adjustKpiGrouping(this.$scope.kpisMap[kpiId].kpi, groupSettingsIdMap);
          });

          this.updateTableAfterGroupRemoval(groupId);
        }
      },
      (error) => (this.$scope.indicators.deletingGroup = { error })
    );
  };

  private updateTableAfterGroupRemoval = (groupId: string) => {
    this.removeGroupFromCollections(groupId);
    this.sortKpisInGroups();
    this.updateKpiTable();
    this.setTableSizes();
    this.$timeout(this.toggleStickyHeaderContent);

    if (this.expandedGroupsIdSet[groupId]) {
      delete this.expandedGroupsIdSet[groupId];
      this.persistExpandedGroupsInStorage();
    }
    if (this.favExpandedGroupsIdSet[groupId]) {
      delete this.favExpandedGroupsIdSet[groupId];
      this.persistExpandedGroupsInStorage();
    }
  };

  private removeGroupFromCollections = (groupId: string): void => {
    delete this.$scope.groupsMap[groupId];
    this.orderedGroupsKpis = this.orderedGroupsKpis.filter((groupSetting) => groupSetting.groupId !== groupId);
    this.$scope.kpiTable.stickyColumn = this.$scope.kpiTable.stickyColumn.filter((cell) => cell.groupId !== groupId);
    this.$scope.kpiTable.columns = this.$scope.kpiTable.columns.map((column) => column.filter((cell) => cell.groupId !== groupId));
  };

  private addGroupToFavorites = (groupId: string, cellId: string): void => {
    this.$scope.indicators.addingGroupToFavorites = { progress: true };
    const addedToFavoritesGroup = <IKpiExistingUiGroup>this.$scope.groupsMap[groupId];

    if (groupId === "mykpis" || groupId === "ungrouped") {
      this.kpiService
        .patchFavoriteSystemGroup(groupId, true)
        .then(() => {
          //  update userSettings held in local storage when adding a system group to favorites
          //  otherwise one would need to login again to get the new settings
          const favoriteSystemGroupsIds = this.currentUserService.getUserSetting("kpiVirtualGroups")?.favorites || [];
          const existingGroupIndex = favoriteSystemGroupsIds.indexOf(groupId);
          if (existingGroupIndex === -1) {
            this.currentUserService.setUserSetting({ kpiVirtualGroups: { favorites: [...favoriteSystemGroupsIds, groupId] } });
          }

          return this.$scope.addGroupToFavoritesHelper(addedToFavoritesGroup);
        })
        .catch((error) => (this.$scope.indicators.addingGroupToFavorites = { error }));
    } else {
      this.kpiService
        .addKpiGroupToFavorites(groupId)
        .then(() => {
          return this.$scope.addGroupToFavoritesHelper(addedToFavoritesGroup);
        })
        .catch((error) => (this.$scope.indicators.addingGroupToFavorites = { error }));
    }

    this.$scope.scrollToCellId = cellId;
  };

  private addGroupToFavoritesHelper = (addedToFavoritesGroup): void => {
    addedToFavoritesGroup.favorite = true;

    this.updateKpiTable();
    this.setTableSizes();
    this.$timeout(this.toggleStickyHeaderContent);

    // make success message stay for a while and then close itself if no interaction
    this.$timeout(() => delete this.$scope.indicators.addingGroupToFavorites, 5000);
  };

  private removeGroupFromFavorites = (groupId: string): void => {
    this.$scope.indicators.removingGroupFromFavorites = { progress: true };
    const removedFromFavoritesGroup = <IKpiExistingUiGroup>this.$scope.groupsMap[groupId];

    if (groupId === "mykpis" || groupId === "ungrouped") {
      this.kpiService
        .patchFavoriteSystemGroup(groupId, false)
        .then(() => {
          //  update userSettings held in local storage when removing a system group from favorites
          //  otherwise one would need to login again to get the new settings
          const favoriteSystemGroupsIds = this.currentUserService.getUserSetting("kpiVirtualGroups")?.favorites || [];
          const existingGroupIndex = favoriteSystemGroupsIds.indexOf(groupId);
          if (existingGroupIndex !== -1) {
            favoriteSystemGroupsIds.splice(existingGroupIndex, 1);
            this.currentUserService.setUserSetting({ kpiVirtualGroups: { favorites: favoriteSystemGroupsIds } });
          }

          return this.$scope.removeGroupFromFavoritesHelper(removedFromFavoritesGroup);
        })
        .catch((error) => (this.$scope.indicators.removingGroupFromFavorites = { error }));
    } else {
      this.kpiService
        .removeKpiGroupFromFavorites(groupId)
        .then(() => {
          return this.$scope.removeGroupFromFavoritesHelper(removedFromFavoritesGroup);
        })
        .catch((error) => (this.$scope.indicators.removingGroupFromFavorites = { error }));
    }
  };

  private removeGroupFromFavoritesHelper = (removedFromFavoritesGroup): void => {
    delete removedFromFavoritesGroup.favorite;

    this.updateKpiTable();
    this.setTableSizes();
    this.$timeout(this.toggleStickyHeaderContent);
    this.persistExpandedGroupsInStorage();

    delete this.$scope.indicators.removingGroupFromFavorites;
  };

  private scrollToFavoriteGroup = (cellId: string): void => {
    document.getElementById("kpi-table").scrollTo({ top: 0, behavior: "smooth" });
    document.getElementById(cellId).classList.add("highlight");
    this.$timeout(() => document.getElementById(cellId).classList.remove("highlight"), 5000);
  };

  private expandFavoriteGroups = (): void => {
    this.$scope.favoriteGroupsExpanded = !this.$scope.favoriteGroupsExpanded;
    if (!this.$scope.favoriteGroupsExpanded) {
      this.$scope.favoriteGroupsWithKpis.forEach((group) => {
        this.$scope.groupsMap[group.groupId].favExpanded = false;
        delete this.favExpandedGroupsIdSet[group.groupId];
      });
    }
    this.updateKpiTable();
    this.setTableSizes();
    this.persistExpandedGroupsInStorage();
    this.$timeout(this.toggleStickyHeaderContent);
  };

  private expandAllGroups = (): void => {
    this.$scope.allGroupsExpanded = !this.$scope.allGroupsExpanded;
    if (!this.$scope.allGroupsExpanded) {
      this.$scope.kpiTable?.stickyColumn.forEach((group) => {
        this.$scope.groupsMap[group.groupId].expanded = false;
        delete this.expandedGroupsIdSet[group.groupId];
      });
    }
    this.updateKpiTable();
    this.setTableSizes();
    this.persistExpandedGroupsInStorage();
    this.$timeout(this.toggleStickyHeaderContent);
  };

  private move = (kpiCell: IKpiCell, action: "top" | "up" | "down" | "bottom", isFavourites) => {
    switch (action) {
      case "top": {
        const moveTop = this.moveTop(kpiCell);
        if (moveTop) {
          this.updateKpiTable();
          this.$timeout(this.toggleStickyHeaderContent);
          this.scrollToAndHighlightCell(kpiCell.id, isFavourites);
        }
        break;
      }
      case "bottom": {
        const moveBottom = this.moveBottom(kpiCell);
        if (moveBottom) {
          this.updateKpiTable();
          this.$timeout(this.toggleStickyHeaderContent);
          this.scrollToAndHighlightCell(kpiCell.id, isFavourites);
        }
        break;
      }
      case "up": {
        const moveUp = this.moveUp(kpiCell);
        if (moveUp) {
          this.updateKpiTable();
          this.$timeout(this.toggleStickyHeaderContent);
          this.scrollToAndHighlightCell(kpiCell.id, isFavourites);
        }
        break;
      }
      case "down": {
        const moveDown = this.moveDown(kpiCell);
        if (moveDown) {
          this.updateKpiTable();
          this.$timeout(this.toggleStickyHeaderContent);
          this.scrollToAndHighlightCell(kpiCell.id, isFavourites);
        }
        break;
      }
    }

    this.analyticsService.track("Button Clicked", { move: this.buildMetadataForKpiMoveTracking(action) });
  };

  private scrollToAndHighlightCell = (kpiCellId: string, isFavourites) => {
    const selector = isFavourites ? `.details-fav-cell-${kpiCellId}` : `.details-cell-${kpiCellId}`;
    const htmlElement = document.querySelector<HTMLElement>(selector);
    htmlElement.classList.add("highlight-move");
    const oldTopPosition = htmlElement.offsetTop;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$timeout(function () {
      if (!ctrl.isElementInView(htmlElement)) {
        const newTopPosition = htmlElement.offsetTop;
        const topScroll = newTopPosition - htmlElement.clientHeight * 2;
        const bottomScroll = newTopPosition - document.getElementById("kpi-table").clientHeight + htmlElement.clientHeight * 2;
        document.getElementById("kpi-table").scrollTo({ top: oldTopPosition > newTopPosition ? topScroll : bottomScroll, behavior: "smooth" });
      }
    }, 1000);

    this.$timeout(function () {
      htmlElement.classList.remove("highlight-move");
    }, 10000);
  };

  private moveTop = (kpiCell: IKpiCell): boolean => {
    const orderedKpiIdsForGroup = this.getGroupKpiIdsForKpiCell(kpiCell);

    if (orderedKpiIdsForGroup.length === 1 || orderedKpiIdsForGroup[0] === kpiCell.kpiId) {
      return false;
    }

    orderedKpiIdsForGroup.splice(orderedKpiIdsForGroup.indexOf(kpiCell.kpiId), 1);
    orderedKpiIdsForGroup.unshift(kpiCell.kpiId);

    this.saveKpiOrder(kpiCell, orderedKpiIdsForGroup);
    return true;
  };

  private moveUp = (kpiCell: IKpiCell): boolean => {
    const orderedKpiIdsForGroup = this.getGroupKpiIdsForKpiCell(kpiCell);

    if (orderedKpiIdsForGroup.length === 1 || orderedKpiIdsForGroup[0] === kpiCell.kpiId) {
      return false;
    }

    const indexOfElementToMove = orderedKpiIdsForGroup.findIndex((kpiId) => kpiId === kpiCell.kpiId);
    const temp = orderedKpiIdsForGroup[indexOfElementToMove];
    orderedKpiIdsForGroup[indexOfElementToMove] = orderedKpiIdsForGroup[indexOfElementToMove - 1];
    orderedKpiIdsForGroup[indexOfElementToMove - 1] = temp;

    this.saveKpiOrder(kpiCell, orderedKpiIdsForGroup);
    return true;
  };

  private moveDown = (kpiCell: IKpiCell): boolean => {
    const orderedKpiIdsForGroup = this.getGroupKpiIdsForKpiCell(kpiCell);
    if (orderedKpiIdsForGroup.length === 1 || orderedKpiIdsForGroup[orderedKpiIdsForGroup.length - 1] === kpiCell.kpiId) {
      return false;
    }

    const indexOfElementToMove = orderedKpiIdsForGroup.findIndex((kpiId) => kpiId === kpiCell.kpiId);
    const temp = orderedKpiIdsForGroup[indexOfElementToMove];
    orderedKpiIdsForGroup[indexOfElementToMove] = orderedKpiIdsForGroup[indexOfElementToMove + 1];
    orderedKpiIdsForGroup[indexOfElementToMove + 1] = temp;

    this.saveKpiOrder(kpiCell, orderedKpiIdsForGroup);
    return true;
  };

  private moveBottom = (kpiCell: IKpiCell): boolean => {
    const orderedKpiIdsForGroup = this.getGroupKpiIdsForKpiCell(kpiCell);
    if (orderedKpiIdsForGroup.length === 1 || orderedKpiIdsForGroup[orderedKpiIdsForGroup.length - 1] === kpiCell.kpiId) {
      return false;
    }

    orderedKpiIdsForGroup.splice(orderedKpiIdsForGroup.indexOf(kpiCell.kpiId), 1);
    orderedKpiIdsForGroup.push(kpiCell.kpiId);

    this.saveKpiOrder(kpiCell, orderedKpiIdsForGroup);
    return true;
  };

  private getGroupKpiIdsForKpiCell = (kpiCell: IKpiCell) => {
    return this.orderedGroupsKpis.find((kpiGroupSetting) => kpiGroupSetting.groupId === kpiCell.groupId).kpiIds;
  };

  private saveKpiOrder = (kpiCell: IKpiCell, orderedKpiIdsForGroup: string[]) => {
    if (kpiCell.groupId === MY_KPIS_GROUP_ID) {
      this.currentUserService.setUserSetting({ myKpiOrder: orderedKpiIdsForGroup });
    } else {
      const kpisToPatch: IModifiableKpi[] = [];

      for (let i = 0; i < orderedKpiIdsForGroup.length; i++) {
        const kpiId = orderedKpiIdsForGroup[i];

        this.$scope.kpisMap[kpiId].kpi.groupsOrder = this.$scope.kpisMap[kpiId].kpi.groupsOrder || {};
        this.$scope.kpisMap[kpiId].kpi.groupsOrder[kpiCell.groupId] = i;

        const modifiedKpi: IModifiableKpi = {};
        modifiedKpi.id = kpiId;
        modifiedKpi.groupsOrder = this.$scope.kpisMap[kpiId].kpi.groupsOrder;

        kpisToPatch.push(modifiedKpi);
      }

      this.kpiService.patchKpis(toKeyMap("id", kpisToPatch));
    }
  };

  private showMoveActions = (kpiCell: IKpiCell, isFavourites) => {
    const selector = isFavourites ? `.move-action-fav-${kpiCell.id} .dropdown-menu` : `.move-action-${kpiCell.id} .dropdown-menu`;
    const dropdownMenu = document.querySelector<HTMLElement>(selector);
    dropdownMenu.style.display = "block";
    dropdownMenu.style.left = "88px";
    dropdownMenu.style.top = "-26px";
  };

  private hideMoveActions = (kpiCell: IKpiCell, isFavourites) => {
    const selector = isFavourites ? `.move-action-fav-${kpiCell.id} .dropdown-menu` : `.move-action-${kpiCell.id} .dropdown-menu`;
    document.querySelector<HTMLElement>(selector).style.display = "none";
  };

  private triggerCellBackgroundColor = (kpiCell: IKpiCell, isFavourites) => {
    if (this.isReorderKpisFeatureEnabled) {
      const selector = isFavourites ? `.details-fav-cell-${kpiCell.id}` : `.details-cell-${kpiCell.id}`;
      const htmlElement = document.querySelector<HTMLElement>(selector);
      const shouldHighlight = !htmlElement.classList.contains("highlight-cell");
      shouldHighlight ? htmlElement.classList.add("highlight-cell") : htmlElement.classList.remove("highlight-cell");
    }
  };

  private buildMetadataForKpiMoveTracking = (action: string): string => {
    switch (action) {
      case "top":
        return "kpis_move_to_top";
      case "up":
        return "kpis_move_up";
      case "down":
        return "kpis_move_down";
      case "bottom":
        return "kpis_move_to_bottom";
    }
  };

  private openKpiGroupingForm = (kpi: IKpi): void => {
    if (!this.hasManageDataPermission()) {
      return;
    }

    this.kpiGroupingFormOpener.open(
      kpi,
      this.orderedGroupsKpis,
      this.$scope.kpisMap,
      this.generateGroupsForGroupsForm(),
      this.beginNewGroup,
      this.isReorderKpisFeatureEnabled
    );
  };

  private generateGroupsForGroupsForm = (): IKpiGroup[] => {
    return this.orderedGroupsKpis.reduce((groups: IKpiGroup[], currentGroupSeting: IKpiGroupSetting): IKpiGroup[] => {
      const groupId: string = currentGroupSeting.groupId;

      if (groupId !== MY_KPIS_GROUP_ID && groupId !== ungroupedGroupId && groupId !== newGroupId) {
        groups.push(this.$scope.groupsMap[groupId]);
      }

      return groups;
    }, []);
  };

  private openConfirmDeleteKpiModal = (kpi: IKpi): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.confirmDeleteKpiModal = this.$uibModal.open({
      templateUrl: require("../views/confirm-delete-kpi.jade"),
      windowClass: "medium-small",
      backdropClass: "dark-backdrop",
      backdrop: true,
      controller: [
        "$scope",
        function ($scope) {
          $scope.kpi = kpi;
          $scope.indicators = ctrl.$scope.indicators;
          $scope.deleteKpi = ctrl.deleteKpi;
        },
      ],
    });
  };

  private deleteKpi = (kpi: IKpi): void => {
    if (!this.hasManageDataPermission()) {
      return;
    }

    this.$scope.kpisMap[kpi.id].loadingSnapshots = true;
    this.$scope.indicators.deletingKpi = { progress: true };

    this.kpiService.deleteKpi(kpi.id).then(
      () => {
        delete this.$scope.indicators.deletingKpi;
        this.confirmDeleteKpiModal.close();

        const groupSettingsIdMap: IdMap<IKpiGroupSetting> = toKeyMap("groupId", this.orderedGroupsKpis);
        const groupsToApplyChangesTo: string[] = [...kpi.groups, ungroupedGroupId, MY_KPIS_GROUP_ID];

        if (this.isReorderKpisFeatureEnabled) {
          const myKpiOrder = this.currentUserService.getUserSetting("myKpiOrder");
          const kpisToPatch: IModifiableKpi[] = [];

          groupsToApplyChangesTo.forEach((groupId): void => {
            if (groupSettingsIdMap[groupId].kpiIds.includes(kpi.id)) {
              const indexOfDeletedKpi = groupSettingsIdMap[groupId].kpiIds.indexOf(kpi.id);
              groupSettingsIdMap[groupId].kpiIds.splice(indexOfDeletedKpi, 1);

              if (groupId === MY_KPIS_GROUP_ID && myKpiOrder) {
                this.currentUserService.setUserSetting({ myKpiOrder: groupSettingsIdMap[groupId].kpiIds });
              } else {
                if (
                  groupSettingsIdMap[groupId].kpiIds.length !== 0 &&
                  this.$scope.kpisMap[groupSettingsIdMap[groupId].kpiIds[0]].kpi.groupsOrder &&
                  this.$scope.kpisMap[groupSettingsIdMap[groupId].kpiIds[0]].kpi.groupsOrder[groupId] !== undefined
                ) {
                  for (let i = 0; i < groupSettingsIdMap[groupId].kpiIds.length; i++) {
                    const kpiToPatch: IModifiableKpi = {};
                    const kpiId = groupSettingsIdMap[groupId].kpiIds[i];
                    this.$scope.kpisMap[kpiId].kpi.groupsOrder = this.$scope.kpisMap[kpiId].kpi.groupsOrder || {};
                    this.$scope.kpisMap[kpiId].kpi.groupsOrder[groupId] = i;

                    kpiToPatch.id = kpiId;
                    kpiToPatch.groupsOrder = this.$scope.kpisMap[kpiId].kpi.groupsOrder;
                    kpisToPatch.push(kpiToPatch);
                  }
                }
              }
            }
          });

          if (kpisToPatch.length > 0) {
            this.kpiService.patchKpis(toKeyMap("id", kpisToPatch)).then(() => {
              this.processTableAfterKpiDelete(kpi.id);
            });
          } else {
            this.processTableAfterKpiDelete(kpi.id);
          }
        } else {
          groupsToApplyChangesTo.forEach((groupId): void => {
            groupSettingsIdMap[groupId].kpiIds = this.removeIdFromIdsCollection(kpi.id, groupSettingsIdMap[groupId].kpiIds);
          });

          this.processTableAfterKpiDelete(kpi.id);
        }
      },
      (error) => {
        this.$scope.kpisMap[kpi.id].loadingSnapshots = false;
        this.$scope.indicators.deletingKpi = { error };
      }
    );
  };

  private processTableAfterKpiDelete = (kpiId: string) => {
    delete this.$scope.kpisMap[kpiId];
    // we need to update kpis table in case any of system groups is emptied
    this.processShowElemets(this.$scope.kpisMap);
    this.updateKpiTable();
    this.setTableSizes();
    this.$timeout(this.toggleStickyHeaderContent);
  };

  private hasManageDataPermission = (): boolean => {
    return this.$rootScope.hasPermission("ManageData");
  };

  private persistExpandedGroupsInStorage = (): void => {
    storage.set(expandedGroupsStorageKey, this.expandedGroupsIdSet);
    storage.set(favExpandedGroupsStorageKey, this.favExpandedGroupsIdSet);
  };

  private setUserKpiSettings = (settings: IKpisScreenSettings): void => {
    // passing just groupBy for as it is the only prop needed atm and this way to and from will be removed from user settings
    this.currentUserService.setUserSetting({ kpis: { groupBy: settings.groupBy } });
  };

  private generateNewSnapshot = (): INewKpiSnapshot => {
    return {
      value: "",
      error: "",
    };
  };

  private isElementInView = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();
    const kpiTable = document.querySelector<HTMLElement>("#kpi-table");

    return rect.top >= 0 && rect.left >= 0 && rect.bottom <= kpiTable.clientHeight && rect.right <= kpiTable.clientHeight;
  };
}
